import type { EHRProvider } from '@heidi/schema/ml-scribe';
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  PaginatedPatientProfilesResult,
  PatientProfileWithSessionCount,
} from '../../contracts';
import { GET_PATIENT_PROFILES, GET_PATIENT_PROFILES_BY_EHR } from '../../contracts';
import { getPatientProfiles, getPatientProfilesV21 } from '../api/patients';

/**
 * The FIRST-PERSON half of `useQueryPatientProfiles` (D2.2, ARCFE-1053).
 *
 * The ownership ruling (Di, 2026-08-12) split the legacy hook by RESOURCE:
 * `/patient-profiles/**` is patients' and moved here;
 * `/teams/{teamId}/members/{memberId}/patient-profiles**` is workspace's and
 * stayed in the app hub as `useQueryTeamMemberPatientProfiles`. The two halves
 * hold SEPARATE cache identities (`GET_PATIENT_PROFILES` vs
 * `GET_TEAM_MEMBER_PATIENT_PROFILES`), which is what makes them two owners
 * rather than one family.
 *
 * The review-mode BRANCH stays app-side. It must: injecting the member call in
 * as a port would put a workspace route inside patients behind a callback —
 * the hidden-command shape design D7 forbids, and one the manifest cannot see
 * because a raw HTTP call to another domain's route is not an import.
 *
 * Every input the legacy body read from the app (Rematch auth, the user
 * record, RBAC, the review-mode atom, the router's session id) arrives here as
 * a plain value composed at the same render-time position by the delegate —
 * the ratified cohort-C shape (`useQueryTemplateById`).
 */
export interface UseQueryOwnPatientProfilesOptions {
  /**
   * Gate for the fetch itself (AND-ed with the auth/scope conditions).
   * Defaults to true so the ~30 existing call sites are unaffected.
   * Callers that mount eagerly but only sometimes need the roster
   * (e.g. the local-search layer in the always-mounted session header)
   * pass false to avoid firing the unpaginated GET /patient-profiles/
   * for nothing. Reads still share the cache when another consumer has
   * fetched it.
   */
  enabled?: boolean;
  /**
   * `!!state.auth.token && !!userData` composed app-side (cohort-C). Two app
   * reads, one decision: a package hook cannot reach the Rematch store or the
   * workspace user record, and injecting either would relocate the coupling
   * rather than remove it.
   */
  isAuthenticated?: boolean;
  /** RBAC fact, app-resolved (S4). */
  isReviewingOtherUser?: boolean;
  /** `isViewingOtherSessionAtom`, app-resolved. */
  isViewingOtherSession?: boolean;
  /** `useCurrentSessionId()`, app-resolved — a router read. */
  currentSessionId?: string | null;
  /**
   * 1-based page number. When provided, the hook fetches a single page via
   * `POST /v2.1/patient-profiles/` and returns `PaginatedPatientProfilesResult`
   * instead of the full sorted array.
   * Callers that omit this receive the original `PatientProfileWithSessionCount[]`.
   */
  page?: number;
  /** Page size. Defaults to 25 when `page` is provided. */
  pageSize?: number;
  /**
   * Debounced search term forwarded as the `q` body field to
   * `POST /v2.1/patient-profiles/`. Omit or pass `undefined` for no filter.
   */
  search?: string;
}

export function useQueryOwnPatientProfiles(
  options: UseQueryOwnPatientProfilesOptions & { page: number }
): UseQueryResult<PaginatedPatientProfilesResult, Error>;
export function useQueryOwnPatientProfiles(
  options?: UseQueryOwnPatientProfilesOptions
): UseQueryResult<PatientProfileWithSessionCount[], Error>;
export function useQueryOwnPatientProfiles({
  enabled = true,
  isAuthenticated = false,
  isReviewingOtherUser = false,
  isViewingOtherSession = false,
  currentSessionId = null,
  page,
  pageSize,
  search,
}: UseQueryOwnPatientProfilesOptions = {}) {
  const directShareSessionId = isViewingOtherSession
    ? currentSessionId || undefined
    : undefined;
  // Disable server pagination for direct-share sessions: POST /v2.1 has no
  // session_id field, so a paginated request would expose profiles outside the
  // shared grant. Fall through to the flag-off arm which forwards session_id.
  const isPaginated = page !== undefined && !isViewingOtherSession;
  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- conditional key: isPaginated is derived from `page` which IS in the paginated key; page/pageSize/search are only used in queryFn when isPaginated is true. Restructuring to a single key would change the flag-off cache key.
    queryKey: isPaginated
      ? [GET_PATIENT_PROFILES, directShareSessionId ?? null, { page, pageSize, search }]
      : [GET_PATIENT_PROFILES, directShareSessionId ?? null],
    enabled: Boolean(
      enabled &&
      isAuthenticated &&
      (!isReviewingOtherUser || (isViewingOtherSession && !!directShareSessionId))
    ),
    placeholderData: isPaginated ? keepPreviousData : undefined,
    queryFn: async (): Promise<
      PatientProfileWithSessionCount[] | PaginatedPatientProfilesResult
    > => {
      if (isPaginated) {
        const { data } = await getPatientProfilesV21({
          page,
          page_size: pageSize ?? 25,
          ...(search ? { q: search } : {}),
        });
        return { patients: data.data, total_pages: data.total_pages };
      }
      const { data } = await getPatientProfiles({ session_id: directShareSessionId });
      // GET /v2 always returns PatientProfileWithSessionCount in practice;
      // the schema also allows PatientProfileModel (no session_count) as a union.
      return (data.data as PatientProfileWithSessionCount[]).sort(
        (a, b) => a.demographic_string?.localeCompare(b.demographic_string || '') ?? 0
      );
    },
  });
}

export interface UseQueryPatientProfilesByEHRProviderOptions {
  ehrProvider: EHRProvider;
  ehrPatientId?: string;
  /**
   * `!!state.auth.token && !!userData` composed app-side (cohort-C) — the same
   * two-reads-one-decision collapse `useQueryOwnPatientProfiles` uses above.
   */
  isAuthenticated?: boolean;
}

/**
 * Look a patient up by the identity an EHR launch handed us (ARCFE-1054).
 *
 * This read was NEVER blocked by the `*MemberPatientProfile` ownership question
 * that held its siblings: it is first-person only — one `GET /patient-profiles/`
 * with `ehr_provider` / `ehr_patient_id` filters, no team-member arm, so there
 * is no branch to keep app-side and nothing to rule on. It sat in the app hub
 * because D2.2 scoped itself to the six branching hooks, not because anything
 * stopped it moving.
 *
 * Its only app coupling was the auth pair, which crosses as one boolean at the
 * same render-time position (cohort-C). `GET_PATIENT_PROFILES_BY_EHR` is a
 * SEPARATE cache identity from `GET_PATIENT_PROFILES`, but NOT because the two
 * strings are unrelated: `'get_patient_profiles'` is in fact a prefix of
 * `'get_patient_profiles_by_ehr'`. That is harmless because TanStack Query
 * matches key ELEMENTS by deep equality, never by substring — a filter of
 * `[GET_PATIENT_PROFILES]` compares its one element against the first element
 * of each key, and `'get_patient_profiles' !== 'get_patient_profiles_by_ehr'`,
 * so the two cannot collide. (`keys.test.ts` pins the no-prefix property for
 * the team-member pair, which is a different pair — it does not, and need not,
 * pin one here.)
 */
export function useQueryPatientProfilesByEHRProvider({
  ehrProvider,
  ehrPatientId,
  isAuthenticated = false,
}: UseQueryPatientProfilesByEHRProviderOptions) {
  return useQuery({
    queryKey: [GET_PATIENT_PROFILES_BY_EHR, { ehrProvider, ehrPatientId }],
    enabled: Boolean(isAuthenticated),
    queryFn: async () => {
      const { data } = await getPatientProfiles({
        ehr_provider: ehrProvider,
        ehr_patient_id: ehrPatientId,
      });
      return data.data.sort((a, b) => {
        return a.demographic_string?.localeCompare(b.demographic_string || '') || 0;
      });
    },
  });
}
